// src/components/ui/TimerBubble.tsx
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { unlockAudio, playAlarm } from "../../lib/sound";

interface TimerBubbleProps {
  duration?: number;
  label?: string;
  announceTime?: boolean;
  onEvent?: (
    event:
      | "timeUp"
      | "finishedEarly"
      | "cantFinish"
      | "paused"
      | "resumed"
      | "extended"
      | "elapsed",
    label?: string,
    duration?: number,
    remaining?: number,
    elapsed?: number
  ) => void;
}

export type TimerBubbleHandle = {
  pause: () => void;
  resume: () => void;
  finish: () => void;
  finishEarly: () => void;
  cantFinish: () => void;
};

const DEFAULT_DURATION = 120;

const TimerBubble = forwardRef<TimerBubbleHandle, TimerBubbleProps>(({
  duration = DEFAULT_DURATION,
  label,
  announceTime = false,
  onEvent,
}, ref) => {
  const safeDuration = duration && duration > 0 ? duration : DEFAULT_DURATION;

  // Countdown + overtime state
  const [timeLeft, setTimeLeft] = useState<number>(safeDuration);
  const [isRunning, setIsRunning] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [isOvertime, setIsOvertime] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [stopped, setStopped] = useState(false);

  const [animate, setAnimate] = useState(false);

  // Refs
  const downIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const overIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const firedTerminalRef = useRef(false);
  const alarmRef = useRef<HTMLAudioElement | null>(null);
  const alarmRepeatTimeoutRef =
    useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingAudibleRef = useRef(false);
  const lastAnnouncedRef = useRef<number | null>(null);
  const announcementRef = useRef<SpeechSynthesisUtterance | null>(null);

  // Unlock audio early
  useEffect(() => {
    const onFirstGesture = async () => {
      try {
        await unlockAudio();
        const a = alarmRef.current;
        if (a) {
          await a.play().catch(() => { });
          a.pause();
          a.currentTime = 0;
        }
      } catch { }
    };
    window.addEventListener("pointerdown", onFirstGesture, { once: true });
    window.addEventListener("touchstart", onFirstGesture, { once: true });
    window.addEventListener("keydown", onFirstGesture, { once: true });
    return () => {
      window.removeEventListener("pointerdown", onFirstGesture);
      window.removeEventListener("touchstart", onFirstGesture);
      window.removeEventListener("keydown", onFirstGesture);
    };
  }, []);

  // Reset on duration change
  useEffect(() => {
    if (downIntervalRef.current) clearInterval(downIntervalRef.current);
    if (overIntervalRef.current) clearInterval(overIntervalRef.current);

    if (alarmRepeatTimeoutRef.current) {
      clearTimeout(alarmRepeatTimeoutRef.current);
      alarmRepeatTimeoutRef.current = null;
    }

    firedTerminalRef.current = false;
    lastAnnouncedRef.current = null;
    setTimeLeft(safeDuration);
    setIsRunning(true);
    setIsPaused(false);
    setIsOvertime(false);
    setElapsed(0);
    setStopped(false);

    // 🔥 FIXED — snapshot alarmRef to satisfy ESLint and avoid undefined variable
    const alarmEl = alarmRef.current;
    if (alarmEl) {
      alarmEl.pause();
      alarmEl.currentTime = 0;
    }

    pendingAudibleRef.current = false;
  }, [safeDuration]);


  // Normal countdown
  useEffect(() => {
    if (isOvertime || !isRunning || isPaused) return;

    const intervalRef = downIntervalRef;
    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          setIsRunning(false);
          setIsOvertime(true);
          setElapsed(0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isOvertime, isRunning, isPaused]);

  // Timer voice announcements
  const shouldAnnounceTime = (secondsLeft: number) => {
    if (secondsLeft <= 0) return false;

    // More than 1 minute: announce exact minute marks.
    // Examples: 3:00, 2:00, 1:00
    if (secondsLeft >= 60) {
      return secondsLeft % 60 === 0;
    }

    // Under 1 minute: announce every 10 seconds.
    // Examples: 50, 40, 30, 20, 10
    return secondsLeft % 10 === 0;
  };

  const speakTimeLeft = (secondsLeft: number) => {
    if (!("speechSynthesis" in window)) return;

    const minutes = Math.floor(secondsLeft / 60);
    const seconds = secondsLeft % 60;

    let text = "";

    if (minutes > 0 && seconds === 0) {
      text = `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
    } else {
      text = `${secondsLeft} seconds`;
    }

    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    announcementRef.current = utterance;
    const clearAnnouncement = () => {
      if (announcementRef.current === utterance) {
        announcementRef.current = null;
      }
    };
    utterance.onend = clearAnnouncement;
    utterance.onerror = clearAnnouncement;
    utterance.rate = 1;
    utterance.pitch = 1;
    utterance.volume = 1;

    window.speechSynthesis.speak(utterance);
  };

  // Stop an in-flight countdown announcement when voice starts connecting.
  // Only cancel when this timer owns an unfinished utterance.
  useEffect(() => {
    if (announceTime || !announcementRef.current) return;
    announcementRef.current = null;
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
  }, [announceTime]);

  // Speak remaining time at therapist-defined intervals
  useEffect(() => {
    //    console.log("[Timer announce check]", {
    //      announceTime,
    //      timeLeft,
    //      isPaused,
    //      isOvertime,
    //      stopped,
    //      shouldAnnounce: shouldAnnounceTime(timeLeft),
    //      hasSpeechSynthesis: "speechSynthesis" in window,
    //    });

    if (!announceTime) return;
    if (isPaused || isOvertime || stopped) return;
    if (!shouldAnnounceTime(timeLeft)) return;
    if (lastAnnouncedRef.current === timeLeft) return;

    console.log("[Timer announce speak]", timeLeft);

    lastAnnouncedRef.current = timeLeft;
    speakTimeLeft(timeLeft);
  }, [announceTime, timeLeft, isPaused, isOvertime, stopped]);

// Alarm logic: play once, wait 10 silent seconds, then repeat.
const scheduleNextAlarm = (): void => {
  if (alarmRepeatTimeoutRef.current) {
    clearTimeout(alarmRepeatTimeoutRef.current);
  }

  alarmRepeatTimeoutRef.current = setTimeout(() => {
    alarmRepeatTimeoutRef.current = null;

    if (document.visibilityState !== "visible") {
      pendingAudibleRef.current = true;
      return;
    }

    tryPlayAlarm();
  }, 10_000);
};

const tryPlayAlarm = (): void => {
  const alarmEl = alarmRef.current;

  if (!alarmEl) {
    playAlarm(3, 200, 0.9);
    scheduleNextAlarm();
    return;
  }

  alarmEl.pause();
  alarmEl.currentTime = 0;
  alarmEl.loop = false;

  alarmEl.onended = () => {
    scheduleNextAlarm();
  };

  alarmEl.play().catch(() => {
    alarmEl.onended = null;
    playAlarm(3, 200, 0.9);
    scheduleNextAlarm();
  });
};

  // Overtime ticking + alarm
  useEffect(() => {
    if (!isOvertime || isPaused || stopped) return;

    if (document.visibilityState !== "visible") {
      pendingAudibleRef.current = true;
    } else {
      tryPlayAlarm();
    }

    if (overIntervalRef.current) clearInterval(overIntervalRef.current);

    overIntervalRef.current = setInterval(() => {
      setElapsed((e) => e + 1);
    }, 1000);

    const onVis = () => {
      if (document.visibilityState === "visible" && pendingAudibleRef.current) {
        pendingAudibleRef.current = false;
        tryPlayAlarm();
      }
    };

    document.addEventListener("visibilitychange", onVis);

    return () => {
      document.removeEventListener("visibilitychange", onVis);

      if (overIntervalRef.current) {
        clearInterval(overIntervalRef.current);
        overIntervalRef.current = null;
      }

      // 🔥 Snapshot alarmRef to avoid ESLint warning
      if (alarmRepeatTimeoutRef.current) {
        clearTimeout(alarmRepeatTimeoutRef.current);
        alarmRepeatTimeoutRef.current = null;
      }
      
      const alarmEl = alarmRef.current;
      if (alarmEl) {
        alarmEl.onended = null;
        alarmEl.pause();
        alarmEl.currentTime = 0;
      }
    };
  }, [isOvertime, isPaused, stopped]);

  // Animation for last 10s
  const inUrgentWindow = timeLeft > 0 && timeLeft <= 10 && !isOvertime;
  useEffect(() => {
    if (!inUrgentWindow) return;
    setAnimate(true);
    const t = setTimeout(() => setAnimate(false), 150);
    return () => clearTimeout(t);
  }, [timeLeft, inUrgentWindow]);

  // Formatting helpers
  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, "0")}:${s
      .toString()
      .padStart(2, "0")}`;
  };

  const formatElapsed = (secs: number) => `+${formatTime(secs)}`;

  const percent = Math.max(
    0,
    Math.min(100, Math.round((timeLeft / safeDuration) * 100))
  );

  const urgencyColor =
    timeLeft <= 5 ? "red" : timeLeft <= 10 ? "orange" : "blue";

  // Button actions
  const handlePause = () => {
    // Keep voice behavior identical to the visible Pause button:
    // only pause during the normal countdown, not after terminal/overtime states.
    if (isPaused || isOvertime || stopped || firedTerminalRef.current) return;

    setIsPaused(true);
    if (downIntervalRef.current) clearInterval(downIntervalRef.current);
    if (overIntervalRef.current) clearInterval(overIntervalRef.current);
    onEvent?.("paused", label, safeDuration, timeLeft, elapsed);
  };

  const handleResume = () => {
    if (!isPaused || isOvertime || stopped || firedTerminalRef.current) return;

    setIsPaused(false);
    onEvent?.("resumed", label, safeDuration, timeLeft, elapsed);
  };

  const handleFinishEarly = () => {
    if (stopped || firedTerminalRef.current) return;

    if (downIntervalRef.current) clearInterval(downIntervalRef.current);
    if (overIntervalRef.current) clearInterval(overIntervalRef.current);

    setIsRunning(false);
    setIsOvertime(false);

    const alarmEl = alarmRef.current;
    if (alarmEl) {
      alarmEl.pause();
      alarmEl.currentTime = 0;
    }

    firedTerminalRef.current = true;
    onEvent?.("finishedEarly", label, safeDuration, timeLeft, elapsed);
  };

  const handleCantFinish = () => {
    if (stopped || firedTerminalRef.current) return;

    if (downIntervalRef.current) clearInterval(downIntervalRef.current);
    if (overIntervalRef.current) clearInterval(overIntervalRef.current);

    setIsRunning(false);
    setIsOvertime(false);

    const alarmEl = alarmRef.current;
    if (alarmEl) {
      alarmEl.pause();
      alarmEl.currentTime = 0;
    }

    firedTerminalRef.current = true;
    onEvent?.("cantFinish", label, safeDuration, timeLeft, elapsed);
  };

  const handleFinishedElapsed = () => {
    setStopped(true);
    if (overIntervalRef.current) {
      clearInterval(overIntervalRef.current);
      overIntervalRef.current = null;
    }
    const alarmEl = alarmRef.current;
    if (alarmEl) {
      alarmEl.pause();
      alarmEl.currentTime = 0;
    }
    if (!firedTerminalRef.current) {
      firedTerminalRef.current = true;
      onEvent?.("elapsed", label, safeDuration, undefined, elapsed);
    }
  };

  // Voice command: "Finished"
  // During countdown, treat it as Finished Early.
  // After time expires, treat it as the elapsed Finished button.
  const handleFinished = () => {
    if (isOvertime) {
      handleFinishedElapsed();
      return;
    }

    handleFinishEarly();
  };

  useImperativeHandle(
    ref,
    () => ({
      pause: handlePause,
      resume: handleResume,
      finish: handleFinished,
      finishEarly: handleFinishEarly,
      cantFinish: handleCantFinish,
    }),
    [
      ref,
      isPaused,
      isOvertime,
      stopped,
      timeLeft,
      elapsed,
      label,
      safeDuration,
      onEvent,
    ]
  );

  const btn =
    "flex-1 px-3 py-2 rounded-lg bg-gray-100 text-gray-900 font-semibold text-sm shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition";

  return (
    <div
      className={`flex flex-col items-center mx-auto w-full max-w-xs sm:max-w-sm border rounded-2xl p-5 shadow-md transition ${isOvertime
        ? "bg-red-50 ring-2 ring-red-400"
        : "bg-white " +
        (inUrgentWindow ? "ring-2 ring-orange-400 ring-offset-2" : "")
        }`}
      aria-live="polite"
    >
      <audio ref={alarmRef} src="/timer-alarm-beeping.wav" preload="auto" />

      <p className="text-lg font-semibold text-gray-800 mb-2">
        {label || "Timer"}
      </p>

      {isOvertime && (
        <p className="text-center text-lg font-semibold text-red-600 mb-1">
          Time’s up
        </p>
      )}

      <p
        className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-3 transform transition-transform duration-150 ${animate ? "scale-110" : "scale-100"
          } ${isOvertime
            ? "text-red-600"
            : urgencyColor === "red"
              ? "text-red-600"
              : urgencyColor === "orange"
                ? "text-orange-500"
                : "text-gray-900"
          }`}
      >
        {isOvertime ? formatElapsed(elapsed) : formatTime(timeLeft)}
      </p>

      {/* BEFORE OVERTIME */}
      {!isOvertime && (
        <>
          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-4 overflow-hidden">
            <div
              className={`h-2 transition-all duration-500 ${urgencyColor === "red"
                ? "bg-red-600"
                : urgencyColor === "orange"
                  ? "bg-orange-500"
                  : "bg-blue-500"
                }`}
              style={{ width: `${percent}%` }}
            />
          </div>

          {/* Pause / Resume */}
          <div className="w-full mb-2">
            {isRunning && !isPaused ? (
              <button onClick={handlePause} className={`${btn} w-full`}>
                Pause
              </button>
            ) : (
              <button onClick={handleResume} className={`${btn} w-full`}>
                Resume
              </button>
            )}
          </div>

          {/* Finished Early */}
          <div className="w-full mb-2">
            <button onClick={handleFinishEarly} className={`${btn} w-full`}>
              Finished Early
            </button>
          </div>

          {/* Can’t Finish */}
          <div className="w-full mb-2">
            <button onClick={handleCantFinish} className={`${btn} w-full`}>
              Can’t Finish
            </button>
          </div>
        </>
      )}

      {/* OVERTIME MODE */}
      {isOvertime && !stopped && (
        <div className="w-full mt-2 space-y-2">
          <button
            onClick={handleFinishedElapsed}
            className="w-full px-4 py-3 rounded-lg bg-blue-600 text-white font-semibold text-base shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
          >
            Finished
          </button>

          <button
            onClick={handleCantFinish}
            className={`${btn} w-full`}
          >
            Can’t Finish
          </button>
        </div>
      )}
    </div>
  );
});

TimerBubble.displayName = "TimerBubble";

export default TimerBubble;
